import { useCallback, useEffect, useRef, useState } from "react";

import { ConsentsPrompt } from "src/components/ConsentsPrompt";
import { ConsentsView } from "src/components/ConsentsView";
import { InsuranceCardPrompt } from "src/components/InsuranceCardPrompt";
import { InsuranceCardUpload } from "src/components/InsuranceCardUpload";
import { getContactPhone } from "src/env";
import { useFeatureFlag } from "src/hooks/useFeatureFlag";
import { useSubmissions } from "src/hooks/useSubmissions";
import { useRequiredConsents } from "src/hooks/useRequiredConsents";
import type { WaitingState } from "src/hooks/useWaitingSSE";
import type { CancellationReason } from "src/api/types";

interface WaitingScreenProps {
  clientId: string;
  streamToken: string;
  state: WaitingState;
}

// Gates the patient insurance card upload and consents while the backend rolls out.
const INSURANCE_CARD_FLAG = "ff-se-3009";

function cancellationMessage(reason: CancellationReason | null): string {
  const contactPhone = getContactPhone();
  switch (reason) {
    case "queue_closed":
      return `The queue has closed for today. Please contact us at ${contactPhone} for assistance.`;
    case "no_show":
      return `Your appointment was marked as no-show. Please call us at ${contactPhone} if you're still here.`;
    case "abandoned":
      return `Your queue entry was cancelled. Please contact us at ${contactPhone} to rejoin.`;
    case "patient_creation_failed":
      return `There was an issue processing your record. Please contact us at ${contactPhone} for assistance.`;
    case "unable_to_complete":
      return `We weren't able to complete your visit today. Please contact us at ${contactPhone} for assistance.`;
    default:
      return `Your queue entry was cancelled. Please contact us at ${contactPhone} for assistance.`;
  }
}

// The backend gives a single estimate; we present it as a range so it doesn't
// read as falsely precise.
function formatEstimateRange(minutes: number): string {
  const rounded = Math.round(minutes);
  if (rounded < 5) return `${rounded}min`;
  return `${rounded - 5}-${rounded}min`;
}

function initialPositionKey(clientId: string): string {
  return `walkin:initial_position:${clientId}`;
}

// Remembers the first position we saw for this queue entry (persisted so a
// page refresh doesn't reset the "how many spots did I jump" count).
function useInitialPosition(clientId: string, patientsAhead: number | null): number | null {
  const [initialPosition, setInitialPosition] = useState<number | null>(null);

  useEffect(() => {
    if (!clientId || patientsAhead === null) return;
    try {
      const key = initialPositionKey(clientId);
      const stored = window.localStorage.getItem(key);
      if (stored !== null) {
        // Syncs from localStorage, an external store, once the first position is known.
        // oxlint-disable-next-line react-hooks/set-state-in-effect
        setInitialPosition(Number(stored));
      } else {
        window.localStorage.setItem(key, String(patientsAhead));
        setInitialPosition(patientsAhead);
      }
    } catch {
      // ignore storage access errors (e.g. private browsing)
    }
  }, [clientId, patientsAhead]);

  return initialPosition;
}

export function WaitingScreen({ clientId, streamToken, state }: WaitingScreenProps) {
  const {
    status,
    cancellationReason,
    connectionState,
    patientsAhead,
    estimatedWaitMinutes,
    lastEventAt,
  } = state;
  const initialPosition = useInitialPosition(clientId, patientsAhead);
  const insuranceEnabled = useFeatureFlag(INSURANCE_CARD_FLAG);
  const {
    insuranceCardSubmitted: submitted,
    consentsSubmitted: agreed,
    markInsuranceSubmitted: markSubmitted,
    markConsentsSubmitted: markAgreed,
  } = useSubmissions(clientId, streamToken, insuranceEnabled);
  const [view, setView] = useState<"main" | "insurance" | "consents">("main");
  const historyPushedRef = useRef(false);

  // The upload view gets its own history entry so the browser back gesture
  // returns to the main view instead of leaving the page.
  useEffect(() => {
    function onPopState() {
      historyPushedRef.current = false;
      setView("main");
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const openInsurance = useCallback(() => {
    window.history.pushState({ insuranceCard: true }, "");
    historyPushedRef.current = true;
    setView("insurance");
  }, []);

  const openConsents = useCallback(() => {
    window.history.pushState({ consents: true }, "");
    historyPushedRef.current = true;
    setView("consents");
  }, []);

  const closeInsurance = useCallback(() => {
    if (historyPushedRef.current) {
      window.history.back();
    } else {
      setView("main");
    }
  }, []);

  const handleSubmitted = useCallback(() => {
    markSubmitted();
    closeInsurance();
  }, [markSubmitted, closeInsurance]);

  const isTerminal = connectionState === "terminal";
  const isError = connectionState === "error" && status === null;
  const isWaiting = status === null || status === "waiting";
  const jumpedSpots =
    initialPosition !== null && patientsAhead !== null ? initialPosition - patientsAhead : 0;
  const hasMovedUp = isWaiting && jumpedSpots >= 1;

  function headline(): string {
    if (status === "completed") return "Visit Complete";
    if (status === "cancelled") return "Visit Canceled";
    if (status === "in_progress") return "Your nurse is ready.";
    if (hasMovedUp) return "You've moved up.";
    return "You're in line.";
  }

  function renderStatusAlert() {
    if (isError) {
      return (
        <div className="status-alert status-alert--warning">
          <p className="status-headline">Waiting to connect</p>
          <p>If this is taking too long… check your connection and refresh this page.</p>
        </div>
      );
    }

    if (isWaiting) {
      const bodyText = hasMovedUp
        ? "You're near the front now. Stay on this page — we'll call you here."
        : "We're holding your place. Stay on this page — it updates on its own.";
      return (
        <div className="status-alert status-alert--success">
          <p>
            {bodyText}
            {connectionState === "reconnecting" && (
              <span className="reconnecting-note"> (Reconnecting…)</span>
            )}
          </p>
        </div>
      );
    }

    if (status === "in_progress") {
      return (
        <div className="status-alert status-alert--success">
          <p>
            We will text you a link to start your visit shortly. Please join within 5 minutes of
            receiving the link. Please call us at {getContactPhone()} if you have any trouble
            joining your visit.
          </p>
        </div>
      );
    }

    if (status === "completed") {
      return (
        <div className="status-alert status-alert--success">
          <p className="status-headline">Your visit is complete.</p>
          <p>Thank you for coming in today.</p>
        </div>
      );
    }

    if (status === "cancelled") {
      return (
        <div className="status-alert status-alert--warning">
          <p className="status-headline">Your queue entry was cancelled.</p>
          <p>{cancellationMessage(cancellationReason)}</p>
        </div>
      );
    }

    return null;
  }

  function formatLastUpdated() {
    if (!lastEventAt) return "never";
    return new Date(lastEventAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  function estimateDisclaimer(): string {
    if (jumpedSpots >= 1) {
      return `You jumped ${jumpedSpots} spot${jumpedSpots === 1 ? "" : "s"}. Keep this page open and your phone close.`;
    }
    return "This is our best estimate and it can move.";
  }

  // The position/estimate stats are only meaningful once we're actually
  // connected and receiving updates.
  const showStatsAndEstimate = isWaiting && !isTerminal && !isError;
  // "When it's your turn" only applies while still waiting.
  const showWhenYourTurn = isWaiting && !isTerminal;
  // "How today works" stays visible through the in-progress state too, and
  // only drops off once the visit reaches a terminal state.
  const showHowTodayWorks = (isWaiting || status === "in_progress") && !isTerminal;
  const showCallUsFooter = isWaiting && !isTerminal;

  const [requiredConsents, setRequiredConsents, consentsState] = useRequiredConsents(
    clientId,
    streamToken,
    insuranceEnabled && showStatsAndEstimate,
  );
  // Backend wins: a non-empty list shows the button even if agreed was remembered.
  const hasRequiredConsents = requiredConsents !== null && requiredConsents.length > 0;
  const showConsentsPrompt =
    insuranceEnabled && showStatsAndEstimate && (hasRequiredConsents || agreed);
  const handleConsentsSubmitted = useCallback(() => {
    markAgreed();
    setRequiredConsents([]);
    closeInsurance();
  }, [markAgreed, setRequiredConsents, closeInsurance]);

  const showConsentsView =
    insuranceEnabled &&
    view === "consents" &&
    hasRequiredConsents &&
    (isWaiting || status === "in_progress");

  const showInsuranceView =
    insuranceEnabled && view === "insurance" && (isWaiting || status === "in_progress");

  return (
    <div className="dashboard">
      <header className="queue-header">
        <img
          src="https://www.eleanorhealth.com/wp-content/uploads/2023/07/logo.svg"
          alt="Eleanor Health"
          className="eleanor-logo"
        />
      </header>

      <div className="queue-body">
        <div className="queue-container">
          <div className="queue-card">
            {showConsentsView ? (
              <ConsentsView
                clientId={clientId}
                streamToken={streamToken}
                status={status}
                consentTypes={requiredConsents ?? []}
                state={consentsState}
                onConsentTypesChange={setRequiredConsents}
                onBack={closeInsurance}
                onSubmitted={handleConsentsSubmitted}
              />
            ) : showInsuranceView ? (
              <InsuranceCardUpload
                clientId={clientId}
                streamToken={streamToken}
                status={status}
                onBack={closeInsurance}
                onSubmitted={handleSubmitted}
              />
            ) : (
              <>
                <h1>{headline()}</h1>

                {renderStatusAlert()}

                {showStatsAndEstimate && (
                  <div className="stats-row">
                    <div className="stat-box">
                      <span className="stat-label">Patients ahead of you</span>
                      <span className="stat-value">
                        {patientsAhead !== null ? patientsAhead : "—"}
                      </span>
                    </div>
                    <div className="stat-box">
                      <span className="stat-label">Estimated wait</span>
                      <span className="stat-value">
                        {estimatedWaitMinutes !== null
                          ? formatEstimateRange(estimatedWaitMinutes)
                          : "—"}
                      </span>
                    </div>
                  </div>
                )}

                {showStatsAndEstimate && (
                  <p className="estimate-disclaimer">{estimateDisclaimer()}</p>
                )}

                {insuranceEnabled && showStatsAndEstimate && (
                  <div className="info-section">
                    <p className="info-section-title">Two things to do while you wait</p>
                    <p className="info-section-text">
                      Finishing both moves you to the front of the line. Once they're done, your
                      nurse can start with you instead of paperwork — and you're more likely to see
                      a prescriber today.
                    </p>
                  </div>
                )}

                {insuranceEnabled && showStatsAndEstimate && (
                  <InsuranceCardPrompt submitted={submitted} onStart={openInsurance} />
                )}

                {showConsentsPrompt && (
                  <ConsentsPrompt agreed={!hasRequiredConsents} onStart={openConsents} />
                )}

                {showHowTodayWorks && (
                  <div className="info-section">
                    <p className="info-section-title">How today works</p>
                    <ol className="info-bullet-list">
                      <li>
                        <p className="info-bullet-title">First, a nurse</p>
                        <p className="info-bullet-text">
                          They'll go through your health history, your medications, and what you're
                          hoping to get out of treatment.
                        </p>
                      </li>
                      <li>
                        <p className="info-bullet-title">Then, a prescriber — often the same day</p>
                        <p className="info-bullet-text">
                          Your nurse books you with a clinician who can prescribe medication before
                          your visit ends. Nurses don't prescribe, and starting here is the quickest
                          way to reach someone who does.
                        </p>
                      </li>
                      <li>
                        <p className="info-bullet-title">Then you're a member</p>
                        <p className="info-bullet-text">
                          Your care team takes it from there — appointments, refills, and support
                          between visits.
                        </p>
                      </li>
                    </ol>
                  </div>
                )}

                {showWhenYourTurn && (
                  <div className="info-section">
                    <p className="info-section-title">When it's your turn</p>
                    <p className="info-section-text">
                      You'll see it here first, and we'll send a text with a link to your video
                      visit. Tap it within 5 minutes so we can hold your spot. Find somewhere
                      private with a decent signal if you can — headphones help.
                    </p>
                  </div>
                )}

                {showCallUsFooter && (
                  <p className="call-us-note">
                    Can't wait today? Call us at {getContactPhone()} — you'll keep everything you've
                    already filled out.
                  </p>
                )}

                <div className="last-updated">Last updated: {formatLastUpdated()}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
